import { ProgressBarBase } from '@orn/shared-web/components/Progress';
import { cx } from '@orn/shared-web/utils/cx';

import { toCurrency } from '../../utils/formatting';

interface ICreditLimitProgressBarProps {
  creditLimit: number;
  balance: number;
  className?: string;
  textClassName?: string;
}

const CreditLimitProgressBar = ({ creditLimit, balance, className, textClassName }: ICreditLimitProgressBarProps) => {
  // If balance is negative (credit on account), show 0% utilization but display actual balance
  const isNegativeBalance = balance < 0;
  const used = isNegativeBalance ? 0 : Math.abs(balance);
  const available = isNegativeBalance ? creditLimit : creditLimit - used;
  const percentageUsed = creditLimit > 0 ? (used / creditLimit) * 100 : 0;
  const roundedPercentage = Math.round(percentageUsed);
  // For display, show actual balance (including negative) to be accurate
  const displayBalance = balance;

  const isVeryHighUtilization = roundedPercentage >= 90;
  const isHighUtilization = roundedPercentage >= 75;
  const progressColorClass = isVeryHighUtilization
    ? 'bg-error-solid'
    : isHighUtilization
      ? 'bg-warning-solid'
      : 'bg-success-solid';
  const usedColorClass = isVeryHighUtilization
    ? 'text-error-primary'
    : isHighUtilization
      ? 'text-warning-primary'
      : 'text-success-primary';

  return (
    <div className={cx('flex flex-col gap-3', className)}>
      <ProgressBarBase
        value={percentageUsed}
        min={0}
        max={100}
        className="h-2"
        progressClassName={progressColorClass}
      />
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 shrink items-center gap-1">
          <span className={cx('text-tertiary text-md shrink-0 font-medium', textClassName)}>
            {isNegativeBalance ? 'Balance:' : 'Used:'}
          </span>
          <span className={cx('text-md font-semibold', usedColorClass, textClassName)}>
            {toCurrency(displayBalance)}
          </span>
        </div>
        <div className="flex min-w-0 shrink items-center gap-1">
          <span className={cx('text-tertiary text-md shrink-0 font-medium', textClassName)}>Available:</span>
          <span className={cx('text-primary text-md font-semibold', textClassName)}>{toCurrency(available)}</span>
        </div>
      </div>
    </div>
  );
};

export default CreditLimitProgressBar;
